import { Injectable } from '@angular/core';
import { Observable, Subscriber, TeardownLogic, Subject } from 'rxjs';
import { HttpHeaders } from '@angular/common/http';
import { UploadStatusService } from './upload/service/status.service';
import { UploadStatusComponent } from './upload/status/status.component';

export enum UploadFileStatus {
  Pending,
  Uploading,
  Completed,
  Failed
}

export class UploadFile {
  name: string;
  size: number;
  param: any;
  progress: number;
  progressObserver: any;
  status: UploadFileStatus;
  file: File;
  subject: Subject<any>;

  public constructor(files: File[], param?: any) {
    if (files && files.length > 0) {
      this.name = files[0].name;
      this.size = files[0].size;
      if (files.length > 1) {
        this.name += ' ... ';
        for (let index = 1; index < files.length; index ++) {
          this.size += files[index].size;
        }
      }
    } else {
      this.name = 'No file attached';
      this.size = 0;
    }
    this.param = param;
    this.progress = 0;
    this.status = UploadFileStatus.Pending;
    this.subject = new Subject<any>();
  }

  public withFile(file: File): UploadFile {
    this.file = file;
    return this;
  }
}

export class UploadObservable extends Observable<any> {
  progress$: any;
  uploadFile: UploadFile;

  public constructor(uploadFile: UploadFile, subscribe?: (this: Observable<any>, subscriber: Subscriber<any>) => TeardownLogic) {
    super(subscribe);

    this.uploadFile = uploadFile;
    if (uploadFile) {
      this.uploadFile.progress = 0;
    }
    this.progress$ = new Observable((observer: any) => {
      if (this.uploadFile) {
        this.uploadFile.progressObserver = observer;
      }
    });

    this.progress$.subscribe((progress: number) => {
      if (this.uploadFile) {
        this.uploadFile.progress = progress;

        if (progress === 100) {
          this.uploadFile.subject.complete();
        } else {
          this.uploadFile.subject.next(progress);
        }
      }
    });
  }
}

@Injectable({
  providedIn: 'root'
})
export class UploadService {
  constructor(
    private uploadStatusService: UploadStatusService
  ) {
  }

  makeFileRequest(url: string, headers: HttpHeaders, name: string, files: File[], method?: string, params?: string[], body?: Object): Observable<any> {
    let upload = null;

    if (files && files.length > 0) {
      upload = new UploadFile(files);
    }

    return new UploadObservable(upload, observer => {
      const formData: FormData = new FormData();
      const xhr: XMLHttpRequest = new XMLHttpRequest();

      // tslint:disable-next-line: prefer-for-of
      for (let i = 0; i < files.length; i++) {
        formData.append(name, files[i], files[i].name);
      }
      if (params) {
        params.forEach(param => {
          const tokens: string[] = param.split('=');
          formData.append(tokens[0], tokens[1]);
        });
      }

      if (body) {
        Object.keys(body).forEach(function(key) {
          const value = body[key];
          if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            formData.append(key, JSON.stringify(value));
          } else if (value instanceof Array && (value.length === 0 || typeof value[0] === 'string')) {
            formData.append(key, body[key]);
          } else {
            formData.append(key, new Blob([JSON.stringify(value)], {
              type: 'application/json'
            }));
          }
        });
      }

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200 || xhr.status === 201) {
            try {
              observer.next(JSON.parse(xhr.response));
            } catch (error) {
              observer.next(xhr.response);
            }
            observer.complete();
          } else {
            observer.error(xhr.response);
          }
        }
      };

      xhr.upload.onprogress = (event) => {
        if (upload) {
          upload.progressObserver.next(Math.round(event.loaded / event.total * 100));
        }
      };

      xhr.open((method ? method : 'POST'), url, true);
      headers.keys().forEach(key => {
        if (key !== 'Content-Type') {
          xhr.setRequestHeader(key, headers.get(key));
        }
      });
      xhr.send(formData);

      /**
       * Add Upload Status
       */
      if (upload) {
        this.uploadStatusService.add(upload, UploadStatusComponent);
      }
    });
  }

  public status() {
    this.uploadStatusService.open(UploadStatusComponent);
  }
}
